import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Link as RouterLink, useParams } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Alert, Box, Breadcrumbs, Button, Link, Skeleton, styled } from '@mui/material';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { Option, pipe } from 'effect';

import { PageHeading } from '../../components/PageHeading';
import { type AdminUser, type SiteAdminUser } from '../../types/User';
import { ScanTypeDetails } from './ScanTypeDetails';

type ScanTypeDetailsViewProps = {
    user: AdminUser | SiteAdminUser;
};

export const ScanTypeDetailsView = ({ user }: ScanTypeDetailsViewProps) => {
    const params = useParams<{
        scanTypeId: string;
    }>();

    const scanTypeId = pipe(
        params.scanTypeId,
        Option.fromNullable,
        Option.map(Number),
        Option.getOrElse(() => -1)
    );

    return (
        <QueryErrorResetBoundary>
            {() => (
                <ErrorBoundary
                    fallbackRender={({ error, resetErrorBoundary }) => (
                        <ErrorPage error={error} resetErrorBoundary={resetErrorBoundary} scanTypeId={scanTypeId} />
                    )}
                >
                    <Suspense fallback={<LoadingPage />}>
                        <ScanTypeDetails scanTypeId={scanTypeId} user={user} />
                    </Suspense>
                </ErrorBoundary>
            )}
        </QueryErrorResetBoundary>
    );
};

const StyledSkeleton = styled(Skeleton)`
    transform: none;
`;

const LoadingPage = () => (
    <Box
        data-testid="skeleton-loader"
        sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            gap: '32px'
        }}
    >
        <StyledSkeleton width="50%" variant="text" sx={{ fontSize: '2.5rem' }} />
        <StyledSkeleton width="100%" />
        <StyledSkeleton width="25rem" height="3.5rem" />
        <StyledSkeleton width="25rem" height="3.5rem" />
        <StyledSkeleton width="25rem" height="3.5rem" />
        <StyledSkeleton width="25rem" height="3.5rem" />
        <StyledSkeleton width="25rem" height="6rem" />
        <StyledSkeleton width="25rem" height="15rem" />
        <StyledSkeleton width="25rem" height="3.5rem" />
        <StyledSkeleton width="25rem" height="3.5rem" />
    </Box>
);

type ErrorPageProps = {
    error: unknown;
    resetErrorBoundary: () => void;
    scanTypeId: number;
};

const ErrorPage = ({ error, resetErrorBoundary, scanTypeId }: ErrorPageProps) => {
    return (
        <Box display="flex" flexDirection="column" gap="32px">
            <Breadcrumbs separator={<ChevronRightIcon />}>
                <Link component={RouterLink} to="/scans/types" underline="hover" color="inherit">
                    <PageHeading>Scan Types</PageHeading>
                </Link>

                <Link component={RouterLink} to={`/scans/types/${scanTypeId}`} underline="hover" color="inherit">
                    <PageHeading>#{scanTypeId}</PageHeading>
                </Link>
            </Breadcrumbs>

            <Alert severity="error">{String(error)}</Alert>

            <Button
                variant="contained"
                color="primary"
                onClick={() => resetErrorBoundary()}
                sx={{ minWidth: '200px', width: 'max-content' }}
            >
                Retry
            </Button>
        </Box>
    );
};
